import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { AbilityCatalogEntry, Role, RolePayload } from "../types";
import { queryKeys } from "./queryKeys";

const baseUrl = "/roles";

const onGetRoles = async (): Promise<Role[]> => {
  const { data } = await axios.get(baseUrl);
  return data;
};

export const useRoles = (enabled: boolean = true) =>
  useQuery<Role[]>({
    queryKey: [queryKeys.ROLES],
    queryFn: onGetRoles,
    enabled,
  });

const onGetRoleAbilities = async (): Promise<AbilityCatalogEntry[]> => {
  const { data } = await axios.get(`${baseUrl}/abilities`);
  return data;
};

export const useRoleAbilities = (enabled: boolean = true) =>
  useQuery<AbilityCatalogEntry[]>({
    queryKey: [queryKeys.ROLE_ABILITIES],
    queryFn: onGetRoleAbilities,
    enabled,
  });

const useRoleMutation = <TArgs>(
  mutationFn: (args: TArgs) => Promise<Role | void>,
  onSuccess?: () => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.ROLES] });
      onSuccess?.();
    },
    onError,
  });
};

export const useCreateRole = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useRoleMutation<RolePayload>(
    async (body) => (await axios.post(baseUrl, body)).data,
    onSuccess,
    onError
  );

export const useUpdateRole = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useRoleMutation<{ id: string } & RolePayload>(
    async ({ id, name, description, abilities }) =>
      (await axios.put(`${baseUrl}/${id}`, { name, description, abilities }))
        .data,
    onSuccess,
    onError
  );

export const useDeleteRole = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) =>
  useRoleMutation<string>(
    async (id) => {
      await axios.delete(`${baseUrl}/${id}`);
    },
    onSuccess,
    onError
  );
